import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type DragEvent,
  type ReactNode,
} from "react";
import { Box, useToast } from "@hightouch/htui";
import { useDropzone } from "react-dropzone";
import { useFileDragFeedback } from "src/components/llm/components/use-file-drag-feedback";
import { FileDragOverlay } from "src/components/llm/components/file-drag-overlay";
import type { FileContextType } from "src/components/llm/context/file-context";

type Receiver = {
  add: FileContextType["addProcessedFiles"];
  disabled: boolean;
};
const ChatFileDropContext = createContext<
  ((receiver: Receiver) => () => void) | null
>(null);

/** Registers from inside the active composer's own file provider, including portaled composers. */
export function useChatFileDropReceiver(
  add: Receiver["add"],
  disabled: boolean,
) {
  const register = useContext(ChatFileDropContext);
  // Store callback refreshes are not composer/store lifecycle changes.
  const latestAdd = useRef(add);
  useEffect(() => {
    latestAdd.current = add;
  }, [add]);
  const receiver = useMemo(
    () => ({ add: (files: File[]) => latestAdd.current(files), disabled }),
    [disabled],
  );
  useEffect(() => register?.(receiver), [register, receiver]);
  return Boolean(register);
}

export function ChatFileDropSurface({
  children,
  disabled,
}: {
  children: ReactNode;
  disabled: boolean;
}) {
  const receiver = useRef<Receiver>();
  const feedback = useFileDragFeedback(disabled);
  const { clear } = feedback;
  const droppedReceivers = useRef(new WeakMap<object, Receiver>());
  const surfaceDisabled = useRef(disabled);
  useEffect(() => {
    surfaceDisabled.current = disabled;
    if (disabled) clear();
  }, [disabled, clear]);
  const { toast } = useToast();
  const register = useCallback(
    (next: Receiver) => {
      receiver.current = next;
      clear();
      return () => {
        if (receiver.current === next) {
          receiver.current = undefined;
          clear();
        }
      };
    },
    [clear],
  );
  const isFileDrag = (event: DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer.types).includes("Files") &&
    event.target instanceof Node &&
    event.currentTarget.contains(event.target);
  const available = () =>
    !surfaceDisabled.current && receiver.current && !receiver.current.disabled;
  const { getRootProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    noDragEventsBubbling: true,
    preventDropOnDocument: false,
    onDrop: (files, _rejections, event) => {
      const destination = droppedReceivers.current.get(event);
      droppedReceivers.current.delete(event);
      // Folder extraction is asynchronous; never deliver into a replacement composer.
      if (!destination || receiver.current !== destination || !available())
        return;
      const attach = async () => {
        try {
          await destination.add(files);
        } catch {
          toast({
            id: "chat-file-drop",
            variant: "error",
            title: "Could not attach files",
            message: "Try adding the files again.",
          });
        }
      };
      void attach();
    },
  });
  const dropRoot = getRootProps();
  return (
    <ChatFileDropContext.Provider value={register}>
      <Box
        ref={dropRoot.ref}
        position="relative"
        height="100%"
        minHeight={0}
        flex={1}
        pt={4}
        px={4}
        onDragOver={(event: DragEvent<HTMLElement>) => {
          if (!isFileDrag(event)) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = available() ? "copy" : "none";
          feedback.show(event, Boolean(available()));
        }}
        onDragLeave={feedback.leave}
        onDrop={(event: DragEvent<HTMLElement>) => {
          if (!isFileDrag(event)) return;
          event.preventDefault();
          clear();
          if (!available() || !receiver.current) {
            event.stopPropagation();
            return;
          }
          // Capture the active store before the existing drop parser walks folders.
          droppedReceivers.current.set(event, receiver.current);
          dropRoot.onDrop?.(event);
        }}
      >
        {children}
        <FileDragOverlay
          isVisible={feedback.active}
          treatment="project"
          heading="Drop files"
          description="Up to 100 files · 1 GB per file · 2 GB total"
          compactDescription="Drop in chat"
        />
      </Box>
    </ChatFileDropContext.Provider>
  );
}
